import { ArrowDownLeft, ArrowUpRight } from "lucide-react";
import type { PortalDealRow } from "@colab/shared";
import { formatPerCurrencyTotals } from "./format";

// Two-card aggregate row at the top of the deals list. Inbound on the left,
// outbound on the right. NEVER blended — even when the layout looks cleaner
// without the split, the partner needs to see their attributed revenue
// separately from outbound flows. Plan quality bar invariant.
//
// Currency: per-currency totals via formatPerCurrencyTotals — no EUR-only
// filter. (The brief proposed EUR-filter but the user override chose match-
// legacy display.)

export function InboundVsOutboundSummary({ rows }: { rows: PortalDealRow[] }) {
  const inbound = rows.filter((r) => r.direction === "inbound");
  const outbound = rows.filter((r) => r.direction === "outbound");
  const inboundTotals = formatPerCurrencyTotals(inbound);
  const outboundTotals = formatPerCurrencyTotals(outbound);

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <SummaryCard
        label="Inbound deals"
        helpText="Deals you sent to Bookboost"
        count={inbound.length}
        totals={inboundTotals}
        Icon={ArrowDownLeft}
        tone="primary"
      />
      <SummaryCard
        label="Outbound flows"
        helpText="Customers Bookboost introduced or you co-influenced"
        count={outbound.length}
        totals={outboundTotals}
        Icon={ArrowUpRight}
        tone="muted"
      />
    </div>
  );
}

function SummaryCard({
  label,
  helpText,
  count,
  totals,
  Icon,
  tone,
}: {
  label: string;
  helpText: string;
  count: number;
  totals: string | null;
  Icon: React.ComponentType<{ className?: string }>;
  tone: "primary" | "muted";
}) {
  return (
    <div className="rounded-xl border bg-card p-4">
      <div className="flex items-center justify-between">
        <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
          {label}
        </p>
        <Icon
          className={
            tone === "primary"
              ? "h-4 w-4 text-accent"
              : "h-4 w-4 text-muted-foreground"
          }
          aria-hidden
        />
      </div>
      <p className="mt-2 font-display text-2xl font-semibold tracking-tight tabular-nums">
        {count}
      </p>
      <p className="mt-1 text-xs text-muted-foreground">{helpText}</p>
      {totals ? (
        <p className="mt-2 text-xs font-medium tabular-nums text-foreground">
          {totals}
        </p>
      ) : null}
    </div>
  );
}
